<template>
    <div class="modal fade show hotel-modal" aria-labelledby="roomHistoryModalTitle">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="d-flex justify-content-between align-items-center px-1 my-1">
                    <h1 class="mb-0">{{ roomName }}</h1>
                    <button @click="closeModal" type="button" class="btn-close" aria-label="Close"></button>
                </div>
                <div class="px-1 range-label">{{ dayLabel }}</div>

                <hr class="mt-0">

                <div class="modal-body">
                    <div v-if="booking" class="booking-card">
                        <div class="booking-card-row">
                            <span class="booking-card-label">Guest Name</span>
                            <span class="booking-card-value">{{ booking.guest_name ? booking.guest_name : '-' }}</span>
                        </div>
                        <div class="booking-card-row">
                            <span class="booking-card-label">Guest Mobile</span>
                            <span class="booking-card-value">{{ booking.guest_mobile ? booking.guest_mobile : '-' }}</span>
                        </div>
                        <div class="booking-card-row">
                            <span class="booking-card-label">Reservation No</span>
                            <span class="booking-card-value">{{ booking.reservation_no }}</span>
                        </div>
                        <div class="booking-card-row">
                            <span class="booking-card-label">Check-in</span>
                            <span class="booking-card-value">{{ booking.check_in }}</span>
                        </div>
                        <div class="booking-card-row">
                            <span class="booking-card-label">Check-out</span>
                            <span class="booking-card-value">{{ booking.check_out }}</span>
                        </div>
                        <div class="booking-card-row">
                            <span class="booking-card-label">Status</span>
                            <span class="booking-card-value">{{ booking.registration_status }}</span>
                        </div>
                        <div class="booking-card-row">
                            <span class="booking-card-label">Notes: </span>
                            <span class="booking-card-value">{{ booking.note ? booking.note : '' }}</span>
                        </div>
                    </div>
                    <p v-else class="text-muted px-1 mb-0">No booking found for this day.</p>

                    <div v-if="canChangeStatus" class="d-flex justify-content-end gap-2 px-1 mt-3">
                        <button type="button" class="btn btn-outline-warning" :disabled="saving" @click="onMarkMaintenance">
                            Mark Maintenance
                        </button>
                        <button type="button" class="btn btn-outline-danger" :disabled="saving" @click="onMarkInactive">
                            Mark Inactive
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, inject, ref } from 'vue';
import { useRoute } from 'vue-router';
import handleHotel from '@/services/modules/hotel/hotel';

const props = defineProps({
    roomId: {
        type: [Number, String],
        required: true,
    },
    roomName: {
        type: String,
        default: '',
    },
    dayKind: {
        type: String,
        default: 'free',
    },
    dayLabel: {
        type: String,
        default: '',
    },
    booking: {
        type: Object,
        default: null,
    },
});

const emit = defineEmits(['onClose', 'onStatusUpdated']);

const $route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { updateHotelRoomStatus } = handleHotel();

const saving = ref(false);

const canChangeStatus = computed(() => props.dayKind === 'free');

const closeModal = () => {
    emit('onClose');
};

const changeStatus = (status, confirmMessage) => {
    if (!confirm(confirmMessage)) return;
    const payload = {
        company_id: $route.params.companyId,
        status,
    };
    saving.value = true;
    updateHotelRoomStatus(payload, props.roomId).then(res => {
        if (!res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        emit('onStatusUpdated');
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        saving.value = false;
    });
};

const onMarkMaintenance = () => {
    changeStatus('maintenance', `Mark ${props.roomName} as under maintenance?`);
};

const onMarkInactive = () => {
    changeStatus('inactive', `Mark ${props.roomName} as inactive?`);
};
</script>

<style scoped>
.hotel-modal {
    display: block;
    background: #1c1d1c33;
}
.range-label {
    font-size: 0.82rem;
    color: #6b7280;
}
.booking-card {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 0.75rem 1rem;
}
.booking-card-row {
    display: flex;
    justify-content: space-between;
    padding: 0.35rem 0;
    border-bottom: 1px solid #f1f5f9;
}
.booking-card-row:last-child {
    border-bottom: none;
}
.booking-card-label {
    font-weight: 600;
    color: #374151;
}
.booking-card-value {
    color: #1f2937;
}
</style>
